import { useRef, useState, useEffect, useCallback } from "react";

interface AudioPlayerProps {
	src: string;
	/** Pre-calculated duration from the API (avoids Chrome MP4 duration bug) */
	initialDuration?: number | null;
	/** Optional className for the outer container */
	className?: string;
	/** Stop click events from bubbling (useful when inside clickable cards) */
	stopPropagation?: boolean;
	/** Callback when audio element metadata is loaded */
	onLoadedMetadata?: (duration: number) => void;
}

function formatTime(time: number) {
	if (isNaN(time) || !isFinite(time)) return "0:00";
	const minutes = Math.floor(time / 60);
	const seconds = Math.floor(time % 60);
	return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export default function AudioPlayer({ src, initialDuration, className, stopPropagation, onLoadedMetadata }: AudioPlayerProps) {
	const audioRef = useRef<HTMLAudioElement>(null);
	const progressBarRef = useRef<HTMLDivElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(initialDuration || 0);
	const [isDragging, setIsDragging] = useState(false);

	const progress = duration > 0 && isFinite(duration) ? Math.min((currentTime / duration) * 100, 100) : 0;

	const hasServerDuration = initialDuration != null && initialDuration >= 1;

	const handleTimeUpdate = () => {
		if (!audioRef.current || isDragging) return;
		const audioDur = audioRef.current.duration;
		if (isFinite(audioDur) && audioDur > 0) {
			setCurrentTime(audioRef.current.currentTime);
			if (!hasServerDuration && !duration) {
				setDuration(audioDur);
			}
		}
	};

	const handleLoadedMetadata = () => {
		if (!audioRef.current) return;
		const audioDur = audioRef.current.duration;
		if (isFinite(audioDur) && audioDur > 0) {
			if (!hasServerDuration) {
				setDuration(audioDur);
			}
			onLoadedMetadata?.(audioDur);
		}
	};

	const handleEnded = () => {
		setIsPlaying(false);
		setCurrentTime(0);
	};

	const togglePlay = (e: React.MouseEvent) => {
		if (stopPropagation) e.stopPropagation();
		if (audioRef.current) {
			if (isPlaying) {
				audioRef.current.pause();
			} else {
				audioRef.current.play();
			}
			setIsPlaying(!isPlaying);
		}
	};

	const seekToPosition = useCallback(
		(clientX: number) => {
			if (progressBarRef.current && audioRef.current) {
				const rect = progressBarRef.current.getBoundingClientRect();
				const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
				const time = ratio * duration;
				audioRef.current.currentTime = time;
				setCurrentTime(time);
			}
		},
		[duration],
	);

	const handleProgressClick = (e: React.MouseEvent) => {
		if (stopPropagation) e.stopPropagation();
		seekToPosition(e.clientX);
	};

	const handleMouseDown = (e: React.MouseEvent) => {
		if (stopPropagation) e.stopPropagation();
		setIsDragging(true);
		seekToPosition(e.clientX);
	};

	// Handle mouse drag for seeking
	useEffect(() => {
		if (!isDragging) return;

		const handleMouseMove = (e: MouseEvent) => {
			seekToPosition(e.clientX);
		};

		const handleMouseUp = () => {
			setIsDragging(false);
		};

		document.addEventListener("mousemove", handleMouseMove);
		document.addEventListener("mouseup", handleMouseUp);

		return () => {
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
		};
	}, [isDragging, seekToPosition]);

	// Reset state when src changes
	useEffect(() => {
		setIsPlaying(false);
		setCurrentTime(0);
		setDuration(initialDuration || 0);
	}, [src, initialDuration]);

	const handleContainerClick = (e: React.MouseEvent) => {
		if (stopPropagation) e.stopPropagation();
	};

	return (
		<div className={`rounded-lg border border-gray-200 bg-gray-50 p-3 ${className ?? ""}`} onClick={handleContainerClick}>
			<audio ref={audioRef} src={src} preload="metadata" onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={handleEnded} />

			<div className="flex items-center gap-3">
				{/* Play/Pause Button */}
				<button
					type="button"
					onClick={togglePlay}
					className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 shadow-sm shrink-0 hover:bg-gray-50 transition-colors cursor-pointer"
					aria-label={isPlaying ? "Pause" : "Play"}
				>
					{isPlaying ? (
						<svg className="w-4 h-4 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
							<path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
						</svg>
					) : (
						<svg className="w-4 h-4 text-gray-700 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
							<path d="M8 5v14l11-7z" />
						</svg>
					)}
				</button>

				{/* Time & Progress */}
				<div className="flex-1 flex items-center gap-2.5 min-w-0">
					<span className="text-xs tabular-nums text-tertiary w-8 text-right shrink-0">{formatTime(currentTime)}</span>
					<div
						ref={progressBarRef}
						className="flex-1 relative h-1.5 rounded-full bg-gray-200 cursor-pointer group"
						onClick={handleProgressClick}
						onMouseDown={handleMouseDown}
					>
						<div className="absolute left-0 top-0 h-full rounded-full bg-gray-800 transition-[width] duration-75" style={{ width: `${progress}%` }} />
						{/* Thumb indicator */}
						<div
							className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-gray-800 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
							style={{ left: `calc(${progress}% - 6px)` }}
						/>
					</div>
					{duration >= 1 && <span className="text-xs tabular-nums text-tertiary w-8 shrink-0">{formatTime(duration)}</span>}
				</div>
			</div>
		</div>
	);
}
